import React, { useMemo } from 'react';
import { CheckCircle2, AlertCircle, Upload } from 'lucide-react';

/**
 * Compliance checklist for the three required documents on every load:
 *   - POD (Proof of Delivery)
 *   - BOL (Bill of Lading)
 *   - Rate Confirmation
 *
 * Renders a horizontal status row at the top of the LoadDocumentsModal so
 * dispatchers/drivers see what's still missing at a glance. Each missing
 * doc has a one-click "Upload" button that pre-selects its doc_kind in the
 * uploader below.
 *
 * Also exports a `<MissingDocsBadge>` used inline on load cards to show
 * "n missing" with red/green badges.
 */
const REQUIRED_DOCS = [
  { kind: 'rate_confirmation', label: 'Rate Con',     full: 'Rate Confirmation' },
  { kind: 'bol',               label: 'BOL',          full: 'Bill of Lading' },
  { kind: 'pod',               label: 'POD',          full: 'Proof of Delivery' },
];

const MissingDocsChecklist = ({ docs, onSelectKind }) => {
  const presence = useMemo(() => {
    const set = new Set((docs || []).map((d) => d.doc_kind));
    return REQUIRED_DOCS.map((r) => ({ ...r, present: set.has(r.kind) }));
  }, [docs]);

  const missingCount = presence.filter((p) => !p.present).length;

  return (
    <div
      className="px-6 py-3 border-b border-slate-100 bg-white"
      data-testid="missing-docs-checklist"
    >
      <div className="flex items-center justify-between mb-2">
        <p
          className="text-xs font-semibold uppercase tracking-widest text-slate-500"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          Compliance checklist
        </p>
        {missingCount === 0 ? (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            data-testid="checklist-status-complete"
          >
            <CheckCircle2 className="w-3 h-3" /> Complete
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-2 py-0.5"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            data-testid="checklist-status-incomplete"
          >
            <AlertCircle className="w-3 h-3" /> {missingCount} missing
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {presence.map((p) => (
          <div
            key={p.kind}
            data-testid={`checklist-item-${p.kind}`}
            className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md border ${
              p.present
                ? 'border-emerald-200 bg-emerald-50/40'
                : 'border-orange-200 bg-orange-50/40'
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {p.present ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p
                  className="text-xs font-semibold text-slate-900 truncate"
                  style={{ fontFamily: 'Work Sans, sans-serif' }}
                >
                  {p.label}
                </p>
                <p
                  className="text-[10px] text-slate-500 truncate"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {p.present ? 'Uploaded' : 'Missing'}
                </p>
              </div>
            </div>
            {!p.present && (
              <button
                type="button"
                data-testid={`checklist-upload-${p.kind}`}
                onClick={() => onSelectKind?.(p.kind)}
                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold text-white bg-[#002FA7] hover:bg-[#00247D]"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <Upload className="w-3 h-3" /> Upload
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Inline badge for load list rows. Lightweight — only shows when there
 * are missing compliance docs. Pass `docs` (array from /loads/{id}/documents).
 *
 * For load lists where we haven't fetched docs yet, pass `docCount` (number)
 * — when 0 we still render the "3 missing" indicator. For full accuracy
 * the parent should fetch /loads/{id}/documents but that's expensive; instead
 * we use the cheap `missing_doc_kinds` projection optionally hydrated from
 * server.
 */
export const MissingDocsBadge = ({ docs, missingKinds, className = '' }) => {
  const missing = useMemo(() => {
    if (Array.isArray(missingKinds)) {
      return missingKinds.filter((k) => REQUIRED_DOCS.some((r) => r.kind === k));
    }
    if (Array.isArray(docs)) {
      const set = new Set(docs.map((d) => d.doc_kind));
      return REQUIRED_DOCS.filter((r) => !set.has(r.kind)).map((r) => r.kind);
    }
    return [];
  }, [docs, missingKinds]);

  if (missing.length === 0) {
    return (
      <span
        data-testid="missing-docs-badge-complete"
        className={`inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5 ${className}`}
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        title="All compliance docs uploaded"
      >
        <CheckCircle2 className="w-3 h-3" /> Docs
      </span>
    );
  }
  return (
    <span
      data-testid="missing-docs-badge-incomplete"
      className={`group relative inline-flex items-center gap-1 text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-full px-1.5 py-0.5 cursor-help ${className}`}
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <AlertCircle className="w-3 h-3" /> {missing.length} missing
      <span
        role="tooltip"
        data-testid="missing-docs-tooltip"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-50 hidden group-hover:block whitespace-nowrap rounded-md bg-slate-900 text-white text-[10px] font-normal px-2.5 py-1.5 shadow-lg"
      >
        <span className="block font-semibold mb-0.5">Still needed to complete this load:</span>
        {missing.map((k) => (
          <span key={k} className="block">• {REQUIRED_DOCS.find((r) => r.kind === k)?.full || k}</span>
        ))}
      </span>
    </span>
  );
};

export default MissingDocsChecklist;
